import { useState, useCallback } from 'react';
import { generateClient } from 'aws-amplify/api';
import { createQuote, getNextQuoteSerial } from '../graphql/mutations';
import { buildQuoteInput } from '../utils/buildQuoteInput';
import { persistCustomPricing } from '../utils/persistCustomPricing';
import { persistCustomMetrics } from '../utils/persistCustomMetrics';
import { recordAuditEvent } from '../utils/auditLog';
import { resolveCciQuotePath } from '../utils/resolveCciQuotePath';
import { generateSwBom } from '../utils/generateBom';
import { runCciQuoteGeneration } from '../utils/runCciQuoteGeneration';
import { generateQuotePDF } from '../utils/quotePdfGenerator';
import { generateRackSlidePPTX } from '../utils/rackSlidePPTGenerator';
import { buildEscapeEvent, recordEscapeEvent, actorFromUser } from '../utils/execPricingAudit';
import { buildMetricsEscapeEvent } from '../utils/execMetricsAudit';
import { hasOverrides } from '../domain/PricingOverrides';
import { hasMetricOverrides } from '../domain/MetricsOverrides';
import { VERSION } from '../version';

const QUOTE_BADGE = 'QUOTE';

/**
 * Quote artifact actions, extracted verbatim from StorageConfiguratorApp
 * (CONF-140 Phase 0b) with two mechanical changes: alert() → showToast with
 * the same text, and the document.createElement "Saving quote..." indicators →
 * a `busy` string rendered by <BusyOverlay />.
 */
export function useQuoteActions({ data, access, exec, config: cfg, ui }) {
  const { config, system, metadata, metrics, bom, pricingData, priceBook, productDb, prices, vendor } = data;
  // CONF-212: customer-facing artifacts print displayMetrics; pricing/BOM/CCI keep `metrics`.
  const displayMetrics = data.displayMetrics ?? metrics;
  const { hasSpecialAccess, user, userName } = access;
  const { execPricing, execMetrics = null, confirmExecEscape } = exec;
  const { updateConfig, batchUpdateConfig } = cfg;
  const { showToast, setQuoteErrors, setShowExportDialog, setShowSaveDialog, validateQuoteField } = ui;

  const [busy, setBusy] = useState(null);
  const fail = (message, title = 'Quote') => showToast({ badge: QUOTE_BADGE, title, message });

  // Escape-audit gates (CONF-106 part 2 amendment, extended CONF-212): every
  // artifact surface that can carry exec-pricing or exec-metrics overrides
  // confirms once per user action and logs one event per active kind.
  const pricingEscapeActive = useCallback(() => hasOverrides(execPricing.overrides), [execPricing.overrides]);
  const metricsEscapeActive = useCallback(
    () => !!execMetrics && hasMetricOverrides(execMetrics.overrides), [execMetrics]);
  const execEscapeActive = useCallback(
    () => pricingEscapeActive() || metricsEscapeActive(), [pricingEscapeActive, metricsEscapeActive]);

  // metricCount only when non-zero: pricing-only callers keep today's exact call shape.
  const confirmEscape = useCallback((artifactLabel) => confirmExecEscape({
    artifactLabel, changeCount: execPricing.changeCount,
    ...(metricsEscapeActive() ? { metricCount: execMetrics.changeCount } : {}),
  }), [confirmExecEscape, execPricing.changeCount, execMetrics, metricsEscapeActive]);

  const logEscape = useCallback(async (artifactType, { quoteSerial = null, quoteName = null } = {}) => {
    const actor = { email: user?.email ?? null, sub: user?.userId ?? null };
    const customerName = metadata.preparedFor ?? config?.preparedFor ?? null;
    let ok = true;
    let failedKind = null;
    if (pricingEscapeActive()) {
      ok = await recordEscapeEvent(buildEscapeEvent(artifactType, {
        execPricing, config, pricingData, productDb: priceBook, bom,
        quoteSerial, customerName, quoteName, actor, vendorId: vendor.id, appVersion: VERSION,
      }));
      if (!ok) failedKind = 'pricing';
    }
    if (ok && metricsEscapeActive()) {
      ok = await recordEscapeEvent(buildMetricsEscapeEvent(artifactType, {
        execMetrics, config, quoteSerial, customerName, quoteName, actor, vendorId: vendor.id, appVersion: VERSION,
      }));
      if (!ok) failedKind = 'metrics';
    }
    if (!ok) {
      // Pricing-only failure keeps the original CONF-106 badge/message (pinned by an
      // existing test); a metrics failure, alone or alongside pricing, uses the
      // broader CONF-212 wording since more than price may be unaudited.
      if (failedKind === 'pricing') {
        showToast({
          badge: 'EXEC PRICING', title: 'Audit log failed',
          message: 'Could not record the pricing audit event — the artifact was not generated.',
        });
      } else {
        showToast({
          badge: 'EXEC OVERRIDES', title: 'Audit log failed',
          message: 'Could not record the override audit event — the artifact was not generated.',
        });
      }
    }
    return ok;
  }, [execPricing, execMetrics, pricingEscapeActive, metricsEscapeActive, config, pricingData, priceBook, bom,
      metadata.preparedFor, user, vendor.id, showToast]);

  const saveQuote = async (description) => {
    try {
      const client = generateClient();
      const quoteId = `quote-${Date.now()}`;
      const timestamp = new Date().toISOString();

      if (!user || !user.username) throw new Error('User information is missing');
      if (!description || description.trim() === '') throw new Error('Quote description is required');

      const configToSave = { ...config };
      if (system.hybrid.enabled) configToSave.hybridEncodingSchemeUserSelected = true;
      if (system.afa.enabled) configToSave.afaEncodingSchemeUserSelected = true;

      const persisted = await persistCustomPricing({
        quoteId, execPricing, config, productDb: priceBook, bom,
        actor: actorFromUser(user),
        vendorId: vendor.id, appVersion: VERSION, savedAt: timestamp,
      });
      if (!persisted.ok) {
        fail('Could not record the custom-pricing audit trail — the quote was not saved.', 'Save failed');
        return;
      }

      const persistedMetrics = await persistCustomMetrics({
        quoteId, execMetrics, config, actor: actorFromUser(user), vendorId: vendor.id, appVersion: VERSION, savedAt: timestamp,
      });
      if (!persistedMetrics.ok) {
        fail('Could not record the custom-metrics audit trail — the quote was not saved.', 'Save failed');
        return;
      }

      const quoteInput = buildQuoteInput({
        userId: user.username, quoteId, description: description.trim(),
        flatConfig: configToSave, metrics: displayMetrics, bom, createdAt: timestamp,
        customPricing: persisted.customPricing, customMetrics: persistedMetrics.customMetrics,
      });

      const response = await client.graphql({ query: createQuote, variables: { input: quoteInput } });
      if (response && response.data && response.data.createQuote) {
        recordAuditEvent({
          eventType: 'config_save', artifactType: 'config', vendor: vendor.id,
          payload: { quoteId, description: description.trim() },
        });
        showToast({ badge: QUOTE_BADGE, title: 'Saved', message: 'Quote saved successfully!' });
        setShowSaveDialog(false);
      } else {
        throw new Error('No data returned from save operation');
      }
    } catch (error) {
      console.error('Error saving quote:', error);
      let errorMessage = 'Unknown error occurred';
      if (error.message) errorMessage = error.message;
      else if (error.errors && error.errors.length > 0) errorMessage = error.errors.map((err) => err.message || err).join(', ');
      else if (error.networkError) errorMessage = `Network error: ${error.networkError.message || error.networkError}`;
      else if (typeof error === 'string') errorMessage = error;
      fail(`Error saving quote: ${errorMessage}`, 'Save failed');
    }
  };

  const handleGenerateRackSlide = async () => {
    if (!metrics) return;
    // I1 review fix: the rack slide printed displayMetrics with no escape
    // gate, so a metrics override could reach a customer artifact unconfirmed
    // and unaudited. Gated on METRICS only (changeCount hardcoded to 0) so
    // pricing-escape behaviour on this surface is unchanged — pricing was
    // never gated here and still isn't.
    if (metricsEscapeActive()) {
      if (!(await confirmExecEscape({
        artifactLabel: 'system slide', changeCount: 0, metricCount: execMetrics.changeCount,
      }))) return;
      if (!(await logEscape('rackSlide'))) return;
    }
    try {
      await generateRackSlidePPTX(displayMetrics, config, productDb);
    } catch (error) {
      console.error('Error generating rack slide:', error);
      fail('Could not generate slide: ' + error.message, 'System Slide');
    }
  };

  const handleGenerateSystemPDF = async () => {
    if (!metrics || !bom) {
      console.error('Metrics or BOM data is not ready');
      return;
    }
    if (execEscapeActive()) {
      if (!(await confirmEscape('system PDF'))) return;
      if (!(await logEscape('quotePdf'))) return;
    }
    try {
      generateQuotePDF(displayMetrics, bom, userName, config, pricingData, false);
    } catch (error) {
      console.error('Error generating PDF:', error);
      fail('Could not generate PDF: ' + error.message, 'Summary PDF');
    }
  };

  /** Open the CSV export dialog after the PDF has had time to generate; auto-close after 3 s. */
  const scheduleExportDialog = () => {
    setTimeout(() => {
      setBusy(null);
      setShowExportDialog(true);
      setTimeout(() => setShowExportDialog(false), 3000);
    }, 4000);
  };

  const handleGenerateQuotePDF = async () => {
    if (!metrics || !bom) {
      console.error('Metrics or BOM data is not ready');
      return;
    }

    const errors = {
      accountExecutive: validateQuoteField('accountExecutive', metadata.accountExecutive),
      preparedFor: validateQuoteField('preparedFor', metadata.preparedFor),
      salesforceQuoteNumber: validateQuoteField('salesforceQuoteNumber', metadata.salesforceQuoteNumber),
      opportunityID: validateQuoteField('opportunityID', metadata.opportunityID),
    };
    if (Object.values(errors).some((e) => e !== '')) {
      setQuoteErrors(errors);
      return;
    }

    const updatedConfig = { ...config };
    if (system.hybrid.enabled) updatedConfig.hybridEncodingSchemeUserSelected = true;
    if (system.afa.enabled) updatedConfig.afaEncodingSchemeUserSelected = true;

    const opportunityId = metadata.opportunityID || metadata.customerID;
    const quoteDesc = metadata.quoteDescription ||
      `${updatedConfig.preparedFor} - ${displayMetrics.totalCompressedEffectiveCapacity.toFixed(0)}TB - ${new Date().toLocaleDateString()}`;

    // ONE confirm for the whole official-quote flow; per-artifact events below.
    if (execEscapeActive() && !(await confirmEscape('official quote (save, PDF, CSV export)'))) {
      return;
    }

    let quoteSerial;
    try {
      const client = generateClient();
      const serialResponse = await client.graphql({ query: getNextQuoteSerial });
      quoteSerial = serialResponse.data.getNextQuoteSerial;
      updatedConfig.configuratorQuoteSerial = quoteSerial;
      updatedConfig.offerNumber = quoteSerial;
      batchUpdateConfig({ offerNumber: quoteSerial, configuratorQuoteSerial: quoteSerial });
    } catch (error) {
      console.error('Error generating quote serial:', error);
      fail('Error generating quote serial number. Please try again.', 'Quote serial');
      return;
    }

    const { quotePath, cciResult, auditFields } = resolveCciQuotePath({
      vendorId: vendor.id, cciEnabled: updatedConfig.cciEnabled, hasSpecialAccess,
      config: updatedConfig, productDb: priceBook, metrics, pricingData, vendor, prices,
    });

    recordAuditEvent({
      eventType: 'quote_generate', artifactType: 'quote',
      quoteSerial: updatedConfig.configuratorQuoteSerial, vendor: vendor.id,
      payload: { description: quoteDesc, opportunityId, ...auditFields },
    });

    if (cciResult?.success) {
      showToast({
        badge: 'CCI', title: 'Compatible Configuration',
        message: 'Generating a combined quote PDF: summary, hardware, and software. You can keep working.',
      });
      const swOnlyRows = generateSwBom(updatedConfig, priceBook, metrics, pricingData, vendor);
      updatedConfig.cciCompatible = true;
      try {
        setBusy('Saving quote...');
        const cciQuoteId = `quote-${Date.now()}`;
        const cciTimestamp = new Date().toISOString();
        const persistedCciMetrics = await persistCustomMetrics({
          quoteId: cciQuoteId, execMetrics, config, actor: actorFromUser(user), vendorId: vendor.id, appVersion: VERSION, savedAt: cciTimestamp,
        });
        if (!persistedCciMetrics.ok) {
          setBusy(null);
          fail('Could not record the custom-metrics audit trail — the quote was not saved.', 'Quote PDF');
          return;
        }
        const cciQuoteInput = {
          input: buildQuoteInput({
            userId: user.username, quoteId: cciQuoteId, description: quoteDesc,
            flatConfig: updatedConfig, metrics: displayMetrics, bom, createdAt: cciTimestamp,
            customMetrics: persistedCciMetrics.customMetrics,
          }),
        };
        if (execEscapeActive() && !(await logEscape('cciQuoteSave', { quoteSerial, quoteName: quoteDesc }))) {
          setBusy(null);
          return;
        }
        const cciClient = generateClient();
        cciClient.graphql({ query: createQuote, variables: cciQuoteInput })
          .then(async () => {
            updateConfig(updatedConfig);
            setBusy('Generating PDFs...');
            if (execEscapeActive() && !(await logEscape('cciPdf', { quoteSerial, quoteName: quoteDesc }))) {
              setBusy(null);
              return;
            }
            try {
              runCciQuoteGeneration({
                metrics: displayMetrics, bom, swRows: swOnlyRows, cciRows: cciResult.rows, userName,
                config: updatedConfig, pricingData, showToast,
              });
              scheduleExportDialog();
            } catch (pdfError) {
              setBusy(null);
              console.error('Error generating CCI PDFs:', pdfError);
              fail('Could not generate PDFs: ' + pdfError.message, 'Quote PDF');
            }
          })
          .catch((error) => {
            setBusy(null);
            console.error('Error saving CCI quote:', error);
            fail(`Error saving quote: ${error.message}`, 'Quote PDF');
          });
      } catch (error) {
        setBusy(null);
        console.error('Error saving CCI quote:', error);
        fail(`Error saving quote: ${error.message}`, 'Quote PDF');
      }
      return;
    }

    if (quotePath === 'cci_fallback') {
      showToast({ badge: 'CCI', title: 'Standard quote generated', message: `${cciResult.reason} Generated a standard quote instead.` });
    }

    setBusy('Saving quote...');
    try {
      const client = generateClient();
      const quoteId = `quote-${Date.now()}`;
      const timestamp = new Date().toISOString();

      const persisted = await persistCustomPricing({
        quoteId, execPricing, config, productDb: priceBook, bom,
        actor: actorFromUser(user), vendorId: vendor.id, appVersion: VERSION, savedAt: timestamp,
      });
      if (!persisted.ok) {
        setBusy(null);
        fail('Could not record the custom-pricing audit trail — the quote was not saved.', 'Quote PDF');
        return;
      }

      const persistedMetrics = await persistCustomMetrics({
        quoteId, execMetrics, config, actor: actorFromUser(user), vendorId: vendor.id, appVersion: VERSION, savedAt: timestamp,
      });
      if (!persistedMetrics.ok) {
        setBusy(null);
        fail('Could not record the custom-metrics audit trail — the quote was not saved.', 'Quote PDF');
        return;
      }

      const quoteInput = {
        input: buildQuoteInput({
          userId: user.username, quoteId, description: quoteDesc, flatConfig: updatedConfig,
          metrics: displayMetrics, bom, createdAt: timestamp, customPricing: persisted.customPricing,
          customMetrics: persistedMetrics.customMetrics,
        }),
      };

      client.graphql({ query: createQuote, variables: quoteInput }).then(async () => {
        setBusy('Generating PDF...');
        updateConfig(updatedConfig);
        if (execEscapeActive() && !(await logEscape('quotePdf', { quoteSerial, quoteName: quoteDesc }))) {
          setBusy(null);
          return;
        }
        try {
          generateQuotePDF(displayMetrics, bom, userName, updatedConfig, pricingData, true);
          setBusy('Preparing CSV export...');
          scheduleExportDialog();
        } catch (pdfError) {
          setBusy(null);
          console.error('Error generating PDF:', pdfError);
          fail('Could not generate PDF: ' + pdfError.message, 'Quote PDF');
        }
      }).catch((error) => {
        setBusy(null);
        console.error('Error saving quote:', error);
        fail(`Error saving quote: ${error.message}`, 'Quote PDF');
      });
    } catch (error) {
      setBusy(null);
      console.error('Error saving quote:', error);
      fail(`Error saving quote: ${error.message}`, 'Quote PDF');
    }
  };

  return { saveQuote, handleGenerateQuotePDF, handleGenerateSystemPDF, handleGenerateRackSlide, execEscapeActive, logEscape, busy };
}

export default useQuoteActions;
